import { api } from './client';

export const LOCATION_LEVELS = ['Zone', 'Aisle', 'Rack', 'Bin'] as const;
export type LocationLevel = (typeof LOCATION_LEVELS)[number];

export interface Location {
  id: number;
  code: string;
  name: string;
  level: LocationLevel;
  parentId: number | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LocationOccupancy {
  location: Location;
  total: number;
  byItem: { itemId: number; code: string; name: string; qty: number }[];
}

export interface LocationAssignmentRow {
  id: string; // BigInt serialised as string
  serialId: number;
  locationId: number;
  placedAt: string;
  placedByGrnId: number | null;
  removedAt: string | null;
  removedByRef: string | null;
  serial: { serialNo: string; itemId: number; status: string };
}

export interface BulkCreateResult {
  created: number;
  skipped: number;
  skippedCodes: string[];
}

/** Exact-code lookup — throws with an error message if not found or inactive. */
export async function lookupLocationByCode(code: string): Promise<Location> {
  const r = await api.get<Location>('/masters/locations/lookup', {
    params: { code },
  });
  return r.data;
}

export async function listLocations(opts: { includeInactive?: boolean; search?: string } = {}): Promise<Location[]> {
  const r = await api.get<Location[]>('/masters/locations', {
    params: {
      ...(opts.includeInactive ? { includeInactive: 'true' } : {}),
      ...(opts.search ? { search: opts.search } : {}),
    },
  });
  return r.data;
}

export async function getLocationOccupancy(id: number): Promise<LocationOccupancy> {
  const r = await api.get<LocationOccupancy>(`/masters/locations/${id}/occupancy`);
  return r.data;
}

export async function getLocationHistory(id: number): Promise<LocationAssignmentRow[]> {
  const r = await api.get<LocationAssignmentRow[]>(`/masters/locations/${id}/history`);
  return r.data;
}

export async function createLocation(dto: {
  code: string;
  name: string;
  level: LocationLevel;
  parentId?: number | null;
  active?: boolean;
}): Promise<Location> {
  const r = await api.post<Location>('/masters/locations', dto);
  return r.data;
}

export async function updateLocation(
  id: number,
  dto: { code?: string; name?: string; active?: boolean },
): Promise<Location> {
  const r = await api.patch<Location>(`/masters/locations/${id}`, dto);
  return r.data;
}

export async function deleteLocation(id: number): Promise<Location> {
  const r = await api.delete<Location>(`/masters/locations/${id}`);
  return r.data;
}

export async function bulkCreateLocations(dto: {
  parentId?: number | null;
  level: LocationLevel;
  codes: string[];
  names?: string[];
}): Promise<BulkCreateResult> {
  const r = await api.post<BulkCreateResult>('/masters/locations/bulk', dto);
  return r.data;
}

export interface LocationImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

export function locationsExportPath(filter: { includeInactive?: boolean; search?: string } = {}): string {
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v != null && v !== '' && v !== false),
    ) as Record<string, string>,
  ).toString();
  return `/masters/locations/export.xlsx${search ? `?${search}` : ''}`;
}

export function locationsImportTemplatePath(): string {
  return '/masters/locations/import-template.xlsx';
}

export async function importLocations(file: File): Promise<LocationImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post<LocationImportResult>('/masters/locations/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000,
  });
  return r.data;
}
